// Referral tracking for partner attribution
// First-touch attribution with 10-day expiry
// No cookies, uses localStorage

const ATTRIBUTION_KEY = 'rentsense_attribution';
const EXPIRY_DAYS = 10;

export interface AttributionData {
  ref: string;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  setAt: number;
}

/**
 * Initialize referral tracking on page load
 * Should be called once on app mount
 */
export function initReferralTracking(): void {
  // Check if stored attribution exists
  const storedData = localStorage.getItem(ATTRIBUTION_KEY);

  if (storedData) {
    try {
      const attribution: AttributionData = JSON.parse(storedData);
      const now = Date.now();
      const expiryMs = EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      // If expired, reset to direct
      if (now > attribution.setAt + expiryMs) {
        storeAttribution({
          ref: 'direct',
          utm_source: null,
          utm_medium: null,
          utm_campaign: null,
          setAt: now,
        });
        return;
      }

      // Not expired, keep existing attribution (first-touch wins)
      return;
    } catch {
      // Invalid JSON, continue to capture new attribution
    }
  }

  // No stored attribution, read from URL
  const urlParams = new URLSearchParams(window.location.search);
  const refParam = urlParams.get('ref');
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  // Store attribution data
  const attribution: AttributionData = {
    ref: refParam?.trim().toLowerCase() || 'direct',
    utm_source: utmSource?.trim().toLowerCase() || null,
    utm_medium: utmMedium?.trim().toLowerCase() || null,
    utm_campaign: utmCampaign?.trim().toLowerCase() || null,
    setAt: Date.now(),
  };

  storeAttribution(attribution);
}

function storeAttribution(data: AttributionData): void {
  localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
}

/**
 * Get the current stored referral value (for backwards compatibility)
 */
export function getStoredRef(): string {
  const attribution = getAttributionData();
  return attribution.ref;
}

/**
 * Get full attribution data for payment tracking
 */
export function getAttributionData(): AttributionData {
  const storedData = localStorage.getItem(ATTRIBUTION_KEY);
  
  if (storedData) {
    try {
      return JSON.parse(storedData);
    } catch {
      // Invalid JSON
    }
  }
  
  return {
    ref: 'direct',
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    setAt: 0,
  };
}

/**
 * @deprecated Use getAttributionData() instead
 */
export interface ReferralInfo {
  ref: string;
  setAt: number;
}

/**
 * @deprecated Use getAttributionData() instead
 */
export function getReferralInfo(): ReferralInfo {
  const attribution = getAttributionData();
  return {
    ref: attribution.ref,
    setAt: attribution.setAt,
  };
}
